import { useEffect, useState } from 'react'
import type { WatchBuildResult } from '../api/client'
import { CORE_SLOTS, isCompleteCompatibleBuild } from '../studioState'

type AssembledPreviewProps = {
  buildResult: WatchBuildResult
  isSending: boolean
  isGenerating: boolean
  imageUrl: string | null
  error: string | null
  onGenerate: () => void
}

export function AssembledPreview({
  buildResult,
  isSending,
  isGenerating,
  imageUrl,
  error,
  onGenerate,
}: AssembledPreviewProps) {
  const [panelOpen, setPanelOpen] = useState(false)
  const [showComplete, setShowComplete] = useState(false)
  const isComplete = isCompleteCompatibleBuild(buildResult)

  useEffect(() => {
    if (isGenerating) {
      setShowComplete(false)
    }
  }, [isGenerating])

  useEffect(() => {
    if (!imageUrl) {
      if (!isGenerating) {
        setShowComplete(false)
      }
      return
    }

    setPanelOpen(true)
    const timer = window.setTimeout(() => {
      setShowComplete(true)
    }, 300)

    return () => window.clearTimeout(timer)
  }, [imageUrl, isGenerating])

  const isDisabled = !isComplete || isSending || isGenerating
  const hasImage = Boolean(imageUrl)

  const missingParts = CORE_SLOTS.filter(
    (partType) => !buildResult.build.parts[partType],
  )
  const incompleteHint = !isComplete
    ? buildResult.failed_steps.length > 0 || buildResult.compatibility_errors.length > 0
      ? 'Adjust the unresolved parts before generating a preview.'
      : missingParts.length > 0
        ? `Add ${missingParts.join(', ')} to enable a preview.`
        : 'Complete the build to enable a preview.'
    : null

  const handleGenerate = () => {
    setShowComplete(false)
    onGenerate()
  }

  return (
    <section className="assembled-preview" aria-labelledby="assembled-preview-heading">
      <div className="preview-trigger-row">
        <div className="preview-trigger-meta">
          <p id="assembled-preview-heading" className="preview-label">Optional studio preview</p>
          <p className="preview-trigger-desc">
            {incompleteHint ?? 'Create a new studio-style image from your selected parts. This usually takes 2–3 minutes.'}
          </p>
        </div>
        <button
          type="button"
          className="preview-trigger-btn"
          onClick={handleGenerate}
          disabled={isDisabled}
        >
          {!hasImage && !error ? (
            <svg className="btn-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2" />
              <rect x="6.5" y="4.5" width="3" height="4" rx="0.5" fill="currentColor" />
              <rect x="6.5" y="10" width="3" height="1.5" rx="0.5" fill="currentColor" />
            </svg>
          ) : null}
          {isGenerating
            ? 'Generating…'
            : hasImage || error
              ? 'Generate a new preview'
              : 'Generate studio preview'}
        </button>
      </div>

      <div className={`preview-panel${panelOpen || hasImage || error ? ' visible' : ''}`}>
        {error ? (
          <div className="assembled-preview-error">
            <p>We could not create the studio preview. Your build is still available.</p>
            <button type="button" className="preview-trigger-btn" onClick={handleGenerate}>
              Try again
            </button>
          </div>
        ) : null}

        {hasImage && showComplete ? (
          <div className="preview-strip visible">
            <div className="preview-text-col">
              <p className="preview-label">Build summary</p>
              <h3 className="preview-desc-title">Your assembled watch</h3>
              <div className="section-rule" />
              <p className="preview-desc-body">
                {buildResult.decisions.length > 0
                  ? buildResult.decisions
                      .slice(0, 3)
                      .map((d) => d.reason)
                      .join(' ')
                  : 'A studio render of your selected case, dial, hands, movement, and strap combined into one cohesive watch.'}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}
